/**
 * Admin Taxonomies API client (CON-CAT-001).
 *
 * Wraps the 11 platform endpoints under `/api/v1/admin/taxonomies/*`:
 *
 *   - GET    /admin/taxonomies                          → {@link listAdminTaxonomies}
 *   - POST   /admin/taxonomies                          → {@link createAdminTaxonomy}
 *   - GET    /admin/taxonomies/:slug                    → {@link getAdminTaxonomy}
 *   - PUT    /admin/taxonomies/:slug                    → {@link updateAdminTaxonomy}
 *   - DELETE /admin/taxonomies/:slug                    → {@link deleteAdminTaxonomy}
 *   - GET    /admin/taxonomies/:slug/nodes              → {@link listAdminTaxonomyNodes}
 *   - POST   /admin/taxonomies/:slug/nodes              → {@link createAdminTaxonomyNode}
 *   - PUT    /admin/taxonomies/:slug/nodes/:nodeId      → {@link updateAdminTaxonomyNode}
 *   - POST   /admin/taxonomies/:slug/nodes/:nodeId/deactivate → {@link deactivateAdminTaxonomyNode}
 *   - POST   /admin/taxonomies/:slug/import             → {@link importAdminTaxonomyNodes}
 *
 * Every helper unwraps the success envelope into the typed payload and
 * translates non-2xx responses into {@link ApiError} via `throwAsApiError`.
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AdminTaxonomyListParams,
  AdminTaxonomyNodeListParams,
  Taxonomy,
  TaxonomyImportData,
  TaxonomyImportReport,
  TaxonomyListData,
  TaxonomyNode,
  TaxonomyNodeListData,
  TaxonomyNodeSingleData,
  TaxonomyNodeWritePayload,
  TaxonomyPaginationMeta,
  TaxonomySingleData,
  TaxonomyWritePayload,
} from '@dfq/shared/types/taxonomy';

import { apiClient } from '../client';
import { throwAsApiError, unwrap } from '../_envelope';

const COLLECTION_PATH = '/admin/taxonomies';

function taxonomyPath(slug: string): string {
  return `${COLLECTION_PATH}/${encodeURIComponent(slug)}`;
}

function nodePath(slug: string, nodeId: number): string {
  return `${taxonomyPath(slug)}/nodes/${encodeURIComponent(String(nodeId))}`;
}

function buildListParams(
  params: AdminTaxonomyListParams,
): Record<string, string | number | boolean | (string | number | boolean)[]> {
  const out: Record<string, string | number | boolean | (string | number | boolean)[]> = {};

  if (params.page !== undefined) out.page = params.page;
  if (params.perPage !== undefined) out.per_page = params.perPage;
  if (params.sortBy !== undefined) out.sort_by = params.sortBy;
  if (params.sortDirection !== undefined) out.sort_direction = params.sortDirection;

  const filters = params.filters ?? {};
  if (filters.search !== undefined && filters.search !== '') {
    out['filters[search]'] = filters.search;
  }
  if (filters.isActive !== undefined) out['filters[is_active]'] = filters.isActive;

  return out;
}

const DEFAULT_PAGINATION: TaxonomyPaginationMeta = {
  page: 1,
  per_page: 25,
  total: 0,
  last_page: 1,
  has_more: false,
};

export interface AdminTaxonomyListResult {
  taxonomies: Taxonomy[];
  pagination: TaxonomyPaginationMeta;
}

export interface AdminTaxonomyNodeListResult {
  nodes: TaxonomyNode[];
  pagination: TaxonomyPaginationMeta;
}

/**
 * `GET /api/v1/admin/taxonomies`
 *
 * Paginated list of taxonomies. Supports search and active/inactive filter.
 */
export async function listAdminTaxonomies(
  params: AdminTaxonomyListParams = {},
): Promise<AdminTaxonomyListResult> {
  try {
    const response = await apiClient.get<ApiEnvelope<TaxonomyListData>>(COLLECTION_PATH, {
      params: buildListParams(params),
    });
    const data = unwrap(response.data, response.status);
    const paginationRaw = response.data.meta?.pagination;
    const pagination =
      paginationRaw && typeof paginationRaw === 'object'
        ? (paginationRaw as TaxonomyPaginationMeta)
        : DEFAULT_PAGINATION;
    return {
      taxonomies: data.taxonomies ?? [],
      pagination,
    };
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `POST /api/v1/admin/taxonomies`
 *
 * Create a new taxonomy. 422 on validation failure.
 */
export async function createAdminTaxonomy(payload: TaxonomyWritePayload): Promise<Taxonomy> {
  try {
    const response = await apiClient.post<ApiEnvelope<TaxonomySingleData>>(
      COLLECTION_PATH,
      payload,
    );
    const data = unwrap(response.data, response.status);
    if (!data.taxonomy) {
      throw new Error('Taxonomy envelope missing `data.taxonomy`');
    }
    return data.taxonomy;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `GET /api/v1/admin/taxonomies/:slug`
 *
 * Fetch a single taxonomy by slug.
 */
export async function getAdminTaxonomy(slug: string): Promise<Taxonomy> {
  try {
    const response = await apiClient.get<ApiEnvelope<TaxonomySingleData>>(taxonomyPath(slug));
    const data = unwrap(response.data, response.status);
    if (!data.taxonomy) {
      throw new Error('Taxonomy envelope missing `data.taxonomy`');
    }
    return data.taxonomy;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `PUT /api/v1/admin/taxonomies/:slug`
 *
 * Update an existing taxonomy.
 */
export async function updateAdminTaxonomy(
  slug: string,
  payload: Partial<TaxonomyWritePayload>,
): Promise<Taxonomy> {
  try {
    const response = await apiClient.put<ApiEnvelope<TaxonomySingleData>>(
      taxonomyPath(slug),
      payload,
    );
    const data = unwrap(response.data, response.status);
    if (!data.taxonomy) {
      throw new Error('Taxonomy envelope missing `data.taxonomy`');
    }
    return data.taxonomy;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `DELETE /api/v1/admin/taxonomies/:slug`
 *
 * Hard-delete a taxonomy. Returns void on success.
 */
export async function deleteAdminTaxonomy(slug: string): Promise<void> {
  try {
    const response = await apiClient.delete<ApiEnvelope<Record<string, never>>>(
      taxonomyPath(slug),
    );
    unwrap(response.data, response.status);
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `GET /api/v1/admin/taxonomies/:slug/nodes`
 *
 * List nodes for a taxonomy. Optionally filter by parentId (pass `null`
 * for root nodes only; omit for all nodes).
 */
export async function listAdminTaxonomyNodes(
  slug: string,
  params: AdminTaxonomyNodeListParams = {},
): Promise<AdminTaxonomyNodeListResult> {
  const queryParams: Record<string, string | number | boolean> = {};
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.perPage !== undefined) queryParams.per_page = params.perPage;
  if (params.parentId !== undefined) {
    queryParams.parent_id = params.parentId === null ? '' : params.parentId;
  }

  try {
    const response = await apiClient.get<ApiEnvelope<TaxonomyNodeListData>>(
      `${taxonomyPath(slug)}/nodes`,
      { params: queryParams },
    );
    const data = unwrap(response.data, response.status);
    const paginationRaw = response.data.meta?.pagination;
    const pagination =
      paginationRaw && typeof paginationRaw === 'object'
        ? (paginationRaw as TaxonomyPaginationMeta)
        : DEFAULT_PAGINATION;
    return {
      nodes: data.nodes ?? [],
      pagination,
    };
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `POST /api/v1/admin/taxonomies/:slug/nodes`
 *
 * Create a node under the given taxonomy.
 */
export async function createAdminTaxonomyNode(
  slug: string,
  payload: TaxonomyNodeWritePayload,
): Promise<TaxonomyNode> {
  try {
    const response = await apiClient.post<ApiEnvelope<TaxonomyNodeSingleData>>(
      `${taxonomyPath(slug)}/nodes`,
      payload,
    );
    const data = unwrap(response.data, response.status);
    if (!data.node) {
      throw new Error('Node envelope missing `data.node`');
    }
    return data.node;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `PUT /api/v1/admin/taxonomies/:slug/nodes/:nodeId`
 *
 * Update an existing taxonomy node.
 */
export async function updateAdminTaxonomyNode(
  slug: string,
  nodeId: number,
  payload: Partial<TaxonomyNodeWritePayload>,
): Promise<TaxonomyNode> {
  try {
    const response = await apiClient.put<ApiEnvelope<TaxonomyNodeSingleData>>(
      nodePath(slug, nodeId),
      payload,
    );
    const data = unwrap(response.data, response.status);
    if (!data.node) {
      throw new Error('Node envelope missing `data.node`');
    }
    return data.node;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `POST /api/v1/admin/taxonomies/:slug/nodes/:nodeId/deactivate`
 *
 * Recursively deactivate a node and every descendant. The platform offers no
 * hard delete here on purpose — a node may already be attached to products,
 * and deactivating keeps those references intact.
 */
export async function deactivateAdminTaxonomyNode(slug: string, nodeId: number): Promise<void> {
  try {
    const response = await apiClient.post<ApiEnvelope<Record<string, never>>>(
      `${nodePath(slug, nodeId)}/deactivate`,
    );
    unwrap(response.data, response.status);
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `POST /api/v1/admin/taxonomies/:slug/import`
 *
 * Bulk import nodes from a CSV or JSON file. Returns a report with
 * imported count, skipped count, and row-level errors.
 */
export async function importAdminTaxonomyNodes(
  slug: string,
  file: File,
): Promise<TaxonomyImportReport> {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await apiClient.post<ApiEnvelope<TaxonomyImportData>>(
      `${taxonomyPath(slug)}/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    const data = unwrap(response.data, response.status);
    if (!data.report) {
      throw new Error('Import envelope missing `data.report`');
    }
    return data.report;
  } catch (error) {
    throwAsApiError(error);
  }
}
